/**
 * Fractional index utilities — v1-compatible numeric midpoint approach.
 *
 * v1 uses plain number indexes with midpoint calculation.
 * Known limitation: precision collapses after ~50 divisions.
 * POC 02 will evaluate string-based alternatives (fractional-indexing).
 *
 * For now we match v1 behavior exactly to validate DnD + optimistic updates.
 */

/**
 * Minimum gap between two indexes. When the gap (|end - start|) is smaller
 * than this, the midpoint would produce a value indistinguishable from one
 * of the neighbors due to floating-point precision. In that case the guard
 * in `nextIndex` forces an offset and sets `indexNeedsRepair` so callers
 * can trigger a container re-index after persisting.
 */
const MIN_INDEX_GAP = 1e-10

/**
 * Flag set when `nextIndex` or `distributeIndexes` detects a precision
 * collapse (neighbors too close or identical). Callers should check this
 * after computing indexes and, if true, schedule an async container repair.
 *
 * Reset to `false` at the start of every `nextIndex` / `distributeIndexes`
 * call so it only reflects the most recent calculation.
 */
export let indexNeedsRepair = false

/**
 * Calculate a new index between two neighbors.
 * If both are provided, returns the midpoint.
 * If only start, returns start + 1.
 * If only end, returns end - 1.
 *
 * **Precision guard:** when `start` and `end` are equal (or closer than
 * `MIN_INDEX_GAP`), the midpoint would produce one of the same values
 * again — an infinite collision loop. The guard detects this and returns
 * `start + 1` instead, which is guaranteed to be unique. It also sets
 * `indexNeedsRepair = true` so the caller can schedule a container re-index.
 */
export function nextIndex(
  start: number | undefined,
  end: number | undefined,
): number {
  indexNeedsRepair = false

  if (start != null && end != null) {
    const gap = Math.abs(end - start)
    if (gap < MIN_INDEX_GAP) {
      // Precision collapse — offset instead of midpoint
      indexNeedsRepair = true
      console.warn(
        `[indexUtils] precision collapse: start=${start}, end=${end}, gap=${gap}. Forcing offset.`,
      )
      return start + 1
    }
    return start + (end - start) / 2
  }
  if (start != null) {
    return start + 1
  }
  if (end != null) {
    return end - 1
  }
  return 0
}

/**
 * Calculate indexes for multiple items being inserted between two boundaries.
 * Distributes items evenly in the available space.
 *
 * @param start - Index of item before insertion point (undefined = beginning)
 * @param end - Index of item after insertion point (undefined = end)
 * @param count - Number of items to distribute
 * @returns Array of calculated indexes
 */
export function distributeIndexes(
  start: number | undefined,
  end: number | undefined,
  count: number,
): number[] {
  if (count === 0) return []
  indexNeedsRepair = false

  const indexes: number[] = []

  if (start != null && end != null) {
    const gap = Math.abs(end - start)
    if (gap < MIN_INDEX_GAP) {
      // Precision collapse — fall through to "start only" logic
      // so each item gets start + 1, start + 2, ...
      indexNeedsRepair = true
      console.warn(
        `[indexUtils] distributeIndexes precision collapse: start=${start}, end=${end}. Using offset.`,
      )
      for (let i = 0; i < count; i++) {
        indexes.push(start + (i + 1))
      }
      return indexes
    }
    const step = (end - start) / (count + 1)
    for (let i = 1; i <= count; i++) {
      indexes.push(start + step * i)
    }
  } else if (start != null) {
    for (let i = 0; i < count; i++) {
      indexes.push(start + (i + 1))
    }
  } else if (end != null) {
    for (let i = 0; i < count; i++) {
      indexes.push(end - (count - i))
    }
  } else {
    for (let i = 0; i < count; i++) {
      indexes.push(i)
    }
  }

  return indexes
}

/**
 * Given an array of items sorted by a numeric key, calculate a new index
 * for moving an item to a specific position.
 *
 * @param items - Array of items in display order
 * @param fromIndex - Current position in array
 * @param toIndex - Target position in array (0-based)
 * @param getKey - Extracts the numeric sort key from an item.
 *                 Defaults to `(item) => item.index` for backward compatibility.
 * @returns New fractional index value
 */
export function reorderIndex<T>(
  items: T[],
  fromIndex: number,
  toIndex: number,
  getKey: (item: T) => number = (item) =>
    (item as Record<string, unknown>).index as number,
): number {
  // Remove the item from its current position for neighbor calculation
  // Matches v1 moveObjects: destination.filter(o => o.id != draggableId)
  const without = items.filter((_, i) => i !== fromIndex)

  // Use toIndex directly — NO adjustment needed.
  // The toIndex is designed so that splice(source, 1) then
  // splice(destination, 0, item) produces the correct result.
  // The same index works for neighbor lookup in the without-array.
  // Works with both @hello-pangea/dnd destination.index and
  // @dnd-kit's over item index.
  const prev =
    toIndex > 0 ? getKey(without[toIndex - 1]!) : undefined
  const next =
    without[toIndex] != null ? getKey(without[toIndex]!) : undefined

  return nextIndex(prev, next)
}
